import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Download } from "lucide-react";
import CleButton from "@cle/components/CleButton";
import {
  useAuthorshipsForInjunction,
  useAuthorshipsForImposition,
} from "@/hooks/useAuthorships";
import {
  useCourseCertificatesForInjunction,
  useCourseCertificatesForImposition,
} from "@/hooks/useCourseCertificates";
import {
  useDownloadAfgorelseDocument,
  usePendingAfgorelseEvidence,
  usePrepareAfgorelseIndberetningForSigning,
  useReportEvidenceByAfgorelse,
} from "@/hooks/useAfgorelse";
import {
  useReductionsForInjunction,
  useReductionsForImposition,
} from "@/hooks/useReductions";
import {
  useLegalAidsForInjunction,
  useLegalAidsForImposition,
} from "@/hooks/useLegalAid";
import {
  useTeachingsForInjunction,
  useTeachingsForImposition,
} from "@/hooks/useTeachings";
import {
  AFGOERELSE_LABELS,
  getCleAfgorelseId,
  type AfgorelseDetailModel,
  type AfgorelseType,
} from "@/interfaces/afgorelse";
import type { PaalaegDetailModel } from "@/interfaces/paalaeg";
import type { PaabudDetailModel } from "@/interfaces/paabud";
import type { SelectableEvidenceRow } from "@cle/models/selectableEvidenceRow";
import { formatDanishLongDate } from "@/util/date";
import { groupEvidenceItemIdsByCategory } from "@cle/utils/groupEvidenceItemIdsByCategory";
import {
  mapAuthorshipToEvidenceRow,
  mapCourseCertificateToEvidenceRow,
  mapReductionToEvidenceRow,
  mapLegalAidToEvidenceRow,
  mapTeachingToEvidenceRow,
} from "@cle/utils/mapToSelectableEvidenceRow";
import { redirectToSigningUrl } from "@cle/utils/redirectToSigningUrl";
import {
  getTotalDroppedLessons,
  getTotalSubmittedLessons,
  getTotalUsableLessons,
} from "@cle/utils/calculateReportCategorySummaries";
import {
  calculateInjunctionCategorySummaries,
  INJUNCTION_CATEGORY_CONFIG,
  mapInjunctionSummariesToPeriodeLektioner,
  toPendingInjunctionOverviewRows,
  type InjunctionCategoryKey,
} from "@cle/utils/calculateInjunctionCategorySummaries";
import {
  getCategoryLimitNotice,
  getCategoryLimitWarning,
  getSurplusLessons,
  getSurplusNotice,
} from "@cle/utils/injunctionReportNotices";

import AwaitingEvidenceWarningModal from "@cle/components/AwaitingEvidenceWarningModal";
import InfoModal from "@cle/components/InfoModal";
import { useAwaitingEvidence } from "@cle/hooks/useAwaitingEvidence";
import AfgorelseInfoContent from "./components/AfgorelseInfoContent";
import InjunctionReportOverview from "./components/InjunctionReportOverview";
import ReportPeriodProgressCard from "../indberetninger/components/ReportPeriodProgressCard";
import ReportSubmissionCard from "../indberetninger/components/ReportSubmissionCard";
import ReportSubmitDialog from "../indberetninger/components/ReportSubmitDialog";

type PendingAfgorelseDetailViewProps = {
  afgorelseType: AfgorelseType;
  detail: (PaabudDetailModel | PaalaegDetailModel) & AfgorelseDetailModel;
};

// Påbud/pålæg har fem kategorier — ingen dispensationer, modsat efteruddannelsesperioden.
type ReportStepKey = InjunctionCategoryKey | "overview";

type ReportStep = {
  description: string;
  key: ReportStepKey;
  shortLabel: string;
  title: string;
};

type ReportCategory = {
  documents: SelectableEvidenceRow[];
  label: string;
};

const createReportSteps = (
  labels: (typeof AFGOERELSE_LABELS)[AfgorelseType],
): ReportStep[] => [
  {
    key: "courseCertificates",
    shortLabel: "Kursusbevis",
    title: "Vælg kursusbeviser",
    description: "Markér de kursusbeviser, du vil bruge som dokumentation.",
  },
  {
    key: "teaching",
    shortLabel: "Undervisning",
    title: "Vælg undervisning",
    description: "Markér den undervisning, du vil bruge som dokumentation.",
  },
  {
    key: "authorship",
    shortLabel: "Forfatterskab",
    title: "Vælg forfatterskab",
    description: "Markér forfatterskab, du vil bruge som dokumentation.",
  },
  {
    key: "reductions",
    shortLabel: "Reduktion",
    title: "Vælg reduktion",
    description: "Markér reduktioner, du vil bruge som dokumentation.",
  },
  {
    key: "legalAid",
    shortLabel: "Advokatvagt/retshjælp",
    title: "Vælg advokatvagt/retshjælp",
    description:
      "Markér dokumentation for advokatvagt eller retshjælp, du vil bruge som dokumentation.",
  },
  {
    key: "overview",
    shortLabel: "Overblik",
    title: labels.overviewStepTitle,
    description: labels.overviewStepDescription,
  },
];

const REPORT_CATEGORIES = INJUNCTION_CATEGORY_CONFIG.reduce(
  (categories, category) => ({
    ...categories,
    [category.key]: { label: category.label, documents: [] },
  }),
  {} as Record<InjunctionCategoryKey, ReportCategory>,
);

const createInitialSelectedDocuments = (): Record<
  InjunctionCategoryKey,
  string[]
> => ({
  courseCertificates: [],
  teaching: [],
  authorship: [],
  reductions: [],
  legalAid: [],
});

const getSelectedLessons = (
  documents: SelectableEvidenceRow[],
  selectedIds: string[],
): number =>
  documents
    .filter((document) => selectedIds.includes(document.id))
    .reduce((total, document) => total + document.lessons, 0);

const getSubmissionErrorMessage = (stepKey: InjunctionCategoryKey): string => {
  const errorMessages: Record<InjunctionCategoryKey, string> = {
    authorship: "Forfatterskab kunne ikke indlæses lige nu.",
    courseCertificates: "Kursusbeviser kunne ikke indlæses lige nu.",
    reductions: "Reduktioner kunne ikke indlæses lige nu.",
    legalAid: "Advokatvagt/retshjælp kunne ikke indlæses lige nu.",
    teaching: "Undervisning kunne ikke indlæses lige nu.",
  };

  return errorMessages[stepKey];
};

const PendingAfgorelseDetailView = ({
  afgorelseType,
  detail,
}: PendingAfgorelseDetailViewProps) => {
  const labels = AFGOERELSE_LABELS[afgorelseType];
  const reportSteps = useMemo(() => createReportSteps(labels), [labels]);
  const isPaabud = afgorelseType === "paabud";
  const isPaalaeg = afgorelseType === "paalaeg";
  const cleAfgorelseId = getCleAfgorelseId(afgorelseType, detail);
  const { mutateAsync: prepareForSigning, isPending: isPreparingForSigning } =
    usePrepareAfgorelseIndberetningForSigning(afgorelseType);
  const { data: reportEvidence = [], isLoading: isReportEvidenceLoading } =
    useReportEvidenceByAfgorelse(afgorelseType, detail.id);
  const { data: pendingEvidence, isLoading: isPendingEvidenceLoading } =
    usePendingAfgorelseEvidence(afgorelseType, detail.indberetningId);
  const { data: awaitingEvidence } = useAwaitingEvidence({
    kind: isPaabud ? "injunction" : "imposition",
    id: cleAfgorelseId,
  });
  const { mutateAsync: downloadDocument, isPending: isDownloadingDocument } =
    useDownloadAfgorelseDocument(afgorelseType);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<
    Record<InjunctionCategoryKey, string[]>
  >(() => createInitialSelectedDocuments());
  const initializedSelectionInjunctionIdRef = useRef<string | null>(null);
  const {
    data: reportableCourseCertificatesForInjunction = [],
    isError: isCourseCertificatesForInjunctionError,
    isLoading: isCourseCertificatesForInjunctionLoading,
  } = useCourseCertificatesForInjunction(cleAfgorelseId, isPaabud);
  const {
    data: reportableCourseCertificatesForImposition = [],
    isError: isCourseCertificatesForImpositionError,
    isLoading: isCourseCertificatesForImpositionLoading,
  } = useCourseCertificatesForImposition(cleAfgorelseId, isPaalaeg);
  const {
    data: reportableReductionsForInjunction = [],
    isError: isReductionsForInjunctionError,
    isLoading: isReductionsForInjunctionLoading,
  } = useReductionsForInjunction(cleAfgorelseId, isPaabud);
  const {
    data: reportableReductionsForImposition = [],
    isError: isReductionsForImpositionError,
    isLoading: isReductionsForImpositionLoading,
  } = useReductionsForImposition(cleAfgorelseId, isPaalaeg);
  const {
    data: reportableLegalAidsForInjunction = [],
    isError: isLegalAidsForInjunctionError,
    isLoading: isLegalAidsForInjunctionLoading,
  } = useLegalAidsForInjunction(cleAfgorelseId, isPaabud);
  const {
    data: reportableLegalAidsForImposition = [],
    isError: isLegalAidsForImpositionError,
    isLoading: isLegalAidsForImpositionLoading,
  } = useLegalAidsForImposition(cleAfgorelseId, isPaalaeg);
  const {
    data: reportableTeachingsForInjunction = [],
    isError: isTeachingsForInjunctionError,
    isLoading: isTeachingsForInjunctionLoading,
  } = useTeachingsForInjunction(cleAfgorelseId, isPaabud);
  const {
    data: reportableTeachingsForImposition = [],
    isError: isTeachingsForImpositionError,
    isLoading: isTeachingsForImpositionLoading,
  } = useTeachingsForImposition(cleAfgorelseId, isPaalaeg);
  const {
    data: reportableAuthorshipsForInjunction = [],
    isError: isAuthorshipsForInjunctionError,
    isLoading: isAuthorshipsForInjunctionLoading,
  } = useAuthorshipsForInjunction(cleAfgorelseId, isPaabud);
  const {
    data: reportableAuthorshipsForImposition = [],
    isError: isAuthorshipsForImpositionError,
    isLoading: isAuthorshipsForImpositionLoading,
  } = useAuthorshipsForImposition(cleAfgorelseId, isPaalaeg);

  const reportableCourseCertificates = isPaabud
    ? reportableCourseCertificatesForInjunction
    : reportableCourseCertificatesForImposition;
  const reportableReductions = isPaabud
    ? reportableReductionsForInjunction
    : reportableReductionsForImposition;
  const reportableLegalAids = isPaabud
    ? reportableLegalAidsForInjunction
    : reportableLegalAidsForImposition;
  const reportableTeachings = isPaabud
    ? reportableTeachingsForInjunction
    : reportableTeachingsForImposition;
  const reportableAuthorships = isPaabud
    ? reportableAuthorshipsForInjunction
    : reportableAuthorshipsForImposition;
  const isCourseCertificatesError = isPaabud
    ? isCourseCertificatesForInjunctionError
    : isCourseCertificatesForImpositionError;
  const isCourseCertificatesLoading = isPaabud
    ? isCourseCertificatesForInjunctionLoading
    : isCourseCertificatesForImpositionLoading;
  const isReductionsError = isPaabud
    ? isReductionsForInjunctionError
    : isReductionsForImpositionError;
  const isReductionsLoading = isPaabud
    ? isReductionsForInjunctionLoading
    : isReductionsForImpositionLoading;
  const isLegalAidsError = isPaabud
    ? isLegalAidsForInjunctionError
    : isLegalAidsForImpositionError;
  const isLegalAidsLoading = isPaabud
    ? isLegalAidsForInjunctionLoading
    : isLegalAidsForImpositionLoading;
  const isTeachingsError = isPaabud
    ? isTeachingsForInjunctionError
    : isTeachingsForImpositionError;
  const isTeachingsLoading = isPaabud
    ? isTeachingsForInjunctionLoading
    : isTeachingsForImpositionLoading;
  const isAuthorshipsError = isPaabud
    ? isAuthorshipsForInjunctionError
    : isAuthorshipsForImpositionError;
  const isAuthorshipsLoading = isPaabud
    ? isAuthorshipsForInjunctionLoading
    : isAuthorshipsForImpositionLoading;

  const courseCertificateDocuments = useMemo(
    () =>
      reportableCourseCertificates.map((certificate) =>
        mapCourseCertificateToEvidenceRow(certificate),
      ),
    [reportableCourseCertificates],
  );
  const reductionDocuments = useMemo(
    () => reportableReductions.map(mapReductionToEvidenceRow),
    [reportableReductions],
  );
  const legalAidDocuments = useMemo(
    () => reportableLegalAids.map(mapLegalAidToEvidenceRow),
    [reportableLegalAids],
  );
  const teachingDocuments = useMemo(
    () => reportableTeachings.map(mapTeachingToEvidenceRow),
    [reportableTeachings],
  );
  const authorshipDocuments = useMemo(
    () => reportableAuthorships.map(mapAuthorshipToEvidenceRow),
    [reportableAuthorships],
  );
  const documentsByCategory = useMemo(
    () => ({
      courseCertificates: courseCertificateDocuments,
      teaching: teachingDocuments,
      authorship: authorshipDocuments,
      reductions: reductionDocuments,
      legalAid: legalAidDocuments,
    }),
    [
      authorshipDocuments,
      courseCertificateDocuments,
      legalAidDocuments,
      reductionDocuments,
      teachingDocuments,
    ],
  );
  const lockedDocumentIdsByCategory = useMemo<
    Record<InjunctionCategoryKey, string[]>
  >(
    () => ({
      courseCertificates: reportableCourseCertificates
        .filter((document) => document.isReported)
        .map((document) => document.id),
      teaching: reportableTeachings
        .filter((document) => document.isReported)
        .map((document) => document.id),
      authorship: reportableAuthorships
        .filter((authorship) => authorship.isReported)
        .map((authorship) => authorship.id),
      reductions: reportableReductions
        .filter((document) => document.isReported)
        .map((document) => document.id),
      legalAid: reportableLegalAids
        .filter((document) => document.isReported)
        .map((document) => document.id),
    }),
    [
      reportableAuthorships,
      reportableCourseCertificates,
      reportableLegalAids,
      reportableReductions,
      reportableTeachings,
    ],
  );
  const reportCategories = useMemo(
    () => ({
      ...REPORT_CATEGORIES,
      courseCertificates: {
        ...REPORT_CATEGORIES.courseCertificates,
        documents: courseCertificateDocuments,
      },
      teaching: {
        ...REPORT_CATEGORIES.teaching,
        documents: teachingDocuments,
      },
      authorship: {
        ...REPORT_CATEGORIES.authorship,
        documents: authorshipDocuments,
      },
      reductions: {
        ...REPORT_CATEGORIES.reductions,
        documents: reductionDocuments,
      },
      legalAid: {
        ...REPORT_CATEGORIES.legalAid,
        documents: legalAidDocuments,
      },
    }),
    [
      authorshipDocuments,
      courseCertificateDocuments,
      legalAidDocuments,
      reductionDocuments,
      teachingDocuments,
    ],
  );
  const currentStep = reportSteps[currentStepIndex];
  const currentSelectableStep =
    currentStep.key === "overview" ? null : currentStep.key;
  const currentCategory = currentSelectableStep
    ? reportCategories[currentSelectableStep]
    : null;
  const selectedLessonsByCategory = useMemo(
    () =>
      Object.entries(reportCategories).reduce(
        (lessonTotals, [key, category]) => {
          const categoryKey = key as InjunctionCategoryKey;

          return {
            ...lessonTotals,
            [categoryKey]: getSelectedLessons(
              category.documents,
              selectedDocumentIds[categoryKey],
            ),
          };
        },
        {} as Record<InjunctionCategoryKey, number>,
      ),
    [reportCategories, selectedDocumentIds],
  );
  const requiredLessons = detail.paakraevedeLektioner;
  const categorySummaries = useMemo(
    () =>
      calculateInjunctionCategorySummaries(
        selectedLessonsByCategory,
        requiredLessons,
      ),
    [requiredLessons, selectedLessonsByCategory],
  );
  // Loftet antal, ikke den rå markering: vælger man 30 lektioner undervisning på et 30-lektioners
  // påbud, indberettes kun 27 (kategoriloftet). Med den rå sum viste kortet 30/30 og
  // advarslen om manglende lektioner udeblev, selvom påbuddet blev underindberettet.
  const countedLessons = getTotalSubmittedLessons(categorySummaries);
  const missingLessons = Math.max(requiredLessons - countedLessons, 0);
  // Overskriften tæller kun lektioner, der bliver til noget — indberettet nu eller
  // overført til næste periode. Det, der bortfalder, står for sig.
  // Kun overskriften: submit-dialogen skal blive på countedLessons, ellers passer
  // "du har valgt X af 12, der mangler Y" ikke sammen.
  const usableLessons = getTotalUsableLessons(categorySummaries);
  const droppedLessons = getTotalDroppedLessons(categorySummaries);
  // Ét opslag fodrer selectedLessons, maxLessons og begge beskeder, så trinnet og
  // overblikket aldrig kan vise hver sit tal.
  const currentSummary = currentSelectableStep
    ? (categorySummaries.find(
        (summary) => summary.key === currentSelectableStep,
      ) ?? null)
    : null;
  const overviewRows = useMemo(
    () => toPendingInjunctionOverviewRows(categorySummaries),
    [categorySummaries],
  );
  const reportedLessons = useMemo(
    () =>
      mapInjunctionSummariesToPeriodeLektioner(
        categorySummaries,
        (summary) => summary.submittedLessons,
      ),
    [categorySummaries],
  );
  const transferredToNextLessons = useMemo(
    () =>
      mapInjunctionSummariesToPeriodeLektioner(
        categorySummaries,
        (summary) => summary.transferredToNext,
      ),
    [categorySummaries],
  );

  useEffect(() => {
    const initializationKey = `${detail.id}:${detail.indberetningId ?? ""}`;

    if (
      initializedSelectionInjunctionIdRef.current === initializationKey ||
      isCourseCertificatesLoading ||
      isReductionsLoading ||
      isTeachingsLoading ||
      isAuthorshipsLoading ||
      isLegalAidsLoading ||
      isReportEvidenceLoading ||
      isPendingEvidenceLoading
    ) {
      return;
    }

    const linkedEvidenceIds = reportEvidence.flatMap((item) =>
      [item.documentId, item.id].filter((id): id is string => Boolean(id)),
    );

    // Kladdens egne valg vinder. CLE-linket dokumentation findes først EFTER en gennemført
    // signering, så uden dette mistede man hele markeringen ved at afbryde MitID.
    const restoredIds = pendingEvidence?.evidenceItemIds.length
      ? pendingEvidence.evidenceItemIds
      : linkedEvidenceIds;

    const restoredSelection = restoredIds.length
      ? groupEvidenceItemIdsByCategory(restoredIds, documentsByCategory)
      : createInitialSelectedDocuments();

    // Låste dokumenter (allerede indberettet) skal med i ALLE grene — tidligere blev de kun
    // lagt på i else-grenen, så en genskabt markering tabte dem lydløst.
    setSelectedDocumentIds(
      INJUNCTION_CATEGORY_CONFIG.reduce(
        (selection, { key }) => ({
          ...selection,
          [key]: Array.from(
            new Set([
              ...lockedDocumentIdsByCategory[key],
              ...restoredSelection[key],
            ]),
          ),
        }),
        {} as Record<InjunctionCategoryKey, string[]>,
      ),
    );

    initializedSelectionInjunctionIdRef.current = initializationKey;
  }, [
    documentsByCategory,
    isAuthorshipsLoading,
    isCourseCertificatesLoading,
    isReportEvidenceLoading,
    isReductionsLoading,
    isLegalAidsLoading,
    isTeachingsLoading,
    lockedDocumentIdsByCategory,
    detail.id,
    detail.indberetningId,
    reportEvidence,
    pendingEvidence,
    isPendingEvidenceLoading,
  ]);

  const handleToggleDocument = (documentId: string) => {
    if (!currentSelectableStep) {
      return;
    }

    if (
      lockedDocumentIdsByCategory[currentSelectableStep].includes(documentId)
    ) {
      return;
    }

    setSelectedDocumentIds((currentIds) => {
      const currentStepIds = currentIds[currentSelectableStep];

      if (currentStepIds.includes(documentId)) {
        return {
          ...currentIds,
          [currentSelectableStep]: currentStepIds.filter(
            (id) => id !== documentId,
          ),
        };
      }

      return {
        ...currentIds,
        [currentSelectableStep]: [...currentStepIds, documentId],
      };
    });
  };

  const handleSelectDocuments = (documentIds: string[]) => {
    if (!currentSelectableStep) {
      return;
    }

    setSelectedDocumentIds((currentIds) => ({
      ...currentIds,
      [currentSelectableStep]: Array.from(
        new Set([
          ...lockedDocumentIdsByCategory[currentSelectableStep],
          ...documentIds,
        ]),
      ),
    }));
  };

  const handlePreviousStep = () => {
    setCurrentStepIndex((stepIndex) => Math.max(stepIndex - 1, 0));
  };

  const handleNextStep = () => {
    setCurrentStepIndex((stepIndex) =>
      Math.min(stepIndex + 1, reportSteps.length - 1),
    );
  };

  const handlePrepareForSigning = async (receiptEmails: string[]) => {
    if (!detail.indberetningId) {
      return;
    }

    const evidenceItemIds = Array.from(
      new Set(Object.values(selectedDocumentIds).flat()),
    );

    const { signingUrl } = await prepareForSigning({
      id: detail.indberetningId,
      afgorelseId: detail.id,
      lektionerIndberettet: reportedLessons,
      lektionerOverfoeresTilNaeste: transferredToNextLessons,
      kvitteringEmails: receiptEmails,
      evidenceItemIds,
    });

    redirectToSigningUrl(signingUrl);
  };

  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownloadDecision = async () => {
    setDownloadError(null);
    let objectUrl: string | null = null;
    try {
      const blob = await downloadDocument(detail.id);
      objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download =
        detail.decisionDocumentFileName ?? labels.downloadFallbackFileName;
      link.click();
    } catch (error) {
      setDownloadError(
        error instanceof Error && error.message
          ? error.message
          : "Dokumentet kunne ikke hentes lige nu. Prøv igen.",
      );
    } finally {
      // Revoke on the next tick so the click-triggered download has started.
      if (objectUrl) {
        const urlToRevoke = objectUrl;
        setTimeout(() => URL.revokeObjectURL(urlToRevoke), 0);
      }
    }
  };

  const reportingDeadline = formatDanishLongDate(detail.frist);
  const documentationPeriod = `${formatDanishLongDate(detail.periodeFra)} - ${formatDanishLongDate(detail.periodeTilMinusEnDag)}`;
  const isOverviewStep = currentStep.key === "overview";
  const isSubmitDisabled = !detail.indberetningId;

  return (
    <div className="cle-report-detail">
      <AwaitingEvidenceWarningModal items={awaitingEvidence} />
      <section className="cle-report-detail__reporting-shell">
        <header className="cle-report-detail__page-header">
          <div>
            <h1 className="cle-report-detail__page-title mb-0">
              {labels.documentationTitle}
            </h1>
            <p className="text-muted small mb-0">
              Vælg de dokumenter, der skal indgå i afgørelsen.
            </p>
          </div>
          <div className="d-flex flex-wrap align-items-center justify-content-end gap-3">
            <p className="cle-report-detail__deadline mb-0">
              Frist {reportingDeadline}
            </p>
            <CleButton
              id={`cle-download-${afgorelseType}`}
              size="sm"
              variant="outline"
              className="gap-2"
              disabled={isDownloadingDocument}
              onClick={handleDownloadDecision}
            >
              <Download size={16} aria-hidden strokeWidth={1.75} />
              {isDownloadingDocument
                ? "Henter..."
                : `Hent ${labels.downloadButtonLabel}`}
            </CleButton>
            {downloadError ? (
              <div className="alert alert-warning mt-2 mb-0" role="alert">
                {downloadError}
              </div>
            ) : null}
          </div>
        </header>

        <div className="cle-report-detail__reporting-body">
          <aside className="cle-report-detail__instruction-panel">
            <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
              <p className="cle-report-detail__step-count mb-0">
                {currentStepIndex + 1} / {reportSteps.length}
              </p>
              <InfoModal
                title="Sådan indberetter du på en afgørelse"
                description="Påbud og pålæg, hvilken dokumentation der kan vælges, kategoriernes maksimum og signering"
                ariaLabel="Læs mere om, hvordan du indberetter på en afgørelse"
              >
                <AfgorelseInfoContent />
              </InfoModal>
            </div>
            <h2 className="cle-report-detail__instruction-title">
              {currentStep.title}
            </h2>
            <p>{currentStep.description}</p>
            {isOverviewStep ? (
              <p>
                Hvis de valgte dokumenter dækker lektionskravet, kan du indsende
                dokumentationen.
              </p>
            ) : (
              <p>
                Nederst i tabellen kan du se, hvor mange lektioner der er valgt
                for dette trin.
              </p>
            )}

            <div className="cle-report-detail__instruction-actions">
              <CleButton
                id={`cle-${afgorelseType}-previous-step`}
                variant="outline"
                className="gap-2"
                disabled={currentStepIndex === 0}
                onClick={handlePreviousStep}
              >
                <ArrowLeft size={16} aria-hidden strokeWidth={1.75} />
                {isOverviewStep ? "" : "Forrige"}
              </CleButton>
              {isOverviewStep ? (
                <ReportSubmitDialog
                  awaitingEvidence={awaitingEvidence}
                  dialogDescription={labels.submitDialogDescription}
                  isPreparing={isPreparingForSigning}
                  isTriggerDisabled={isSubmitDisabled}
                  missingLessons={missingLessons}
                  onPrepareForSigning={handlePrepareForSigning}
                  requiredLessons={requiredLessons}
                  selectedLessons={countedLessons}
                  triggerId={`cle-submit-${afgorelseType}`}
                  triggerLabel="Godkend og indsend"
                />
              ) : (
                <CleButton
                  id={`cle-${afgorelseType}-next-step`}
                  className="gap-2"
                  onClick={handleNextStep}
                >
                  Næste
                  <ArrowRight size={16} aria-hidden strokeWidth={1.75} />
                </CleButton>
              )}
            </div>
          </aside>

          <div className="cle-report-detail__workspace">
            <ReportPeriodProgressCard
              deadline={reportingDeadline}
              documentationPeriod={documentationPeriod}
              missingLessons={missingLessons}
              periodRange={
                detail.decisionDocumentFileName ??
                labels.decisionDocumentFallback
              }
              requiredLessons={requiredLessons}
              selectedLessons={usableLessons}
              droppedLessons={droppedLessons}
              title={labels.progressCardTitle}
              transferredOutLessons={transferredToNextLessons.sum}
            />
            {!detail.indberetningId ? (
              <div className="alert alert-info mb-0" role="status">
                {labels.noOpenIndberetningMessage}
              </div>
            ) : null}

            {currentCategory && currentSelectableStep ? (
              <ReportSubmissionCard
                description={`Vælg de dokumenter, der skal tælle med som ${currentCategory.label.toLowerCase()}.`}
                documents={currentCategory.documents}
                errorMessage={getSubmissionErrorMessage(currentSelectableStep)}
                maxLessons={currentSummary?.maxReported}
                noticeMessage={getCategoryLimitNotice(currentSummary)}
                warningMessage={getCategoryLimitWarning(currentSummary)}
                evidenceStepKey={currentSelectableStep}
                isError={
                  (currentSelectableStep === "courseCertificates" &&
                    isCourseCertificatesError) ||
                  (currentSelectableStep === "reductions" &&
                    isReductionsError) ||
                  (currentSelectableStep === "legalAid" && isLegalAidsError) ||
                  (currentSelectableStep === "teaching" && isTeachingsError) ||
                  (currentSelectableStep === "authorship" && isAuthorshipsError)
                }
                isLoading={
                  (currentSelectableStep === "courseCertificates" &&
                    isCourseCertificatesLoading) ||
                  (currentSelectableStep === "reductions" &&
                    isReductionsLoading) ||
                  (currentSelectableStep === "legalAid" &&
                    isLegalAidsLoading) ||
                  (currentSelectableStep === "teaching" &&
                    isTeachingsLoading) ||
                  (currentSelectableStep === "authorship" &&
                    isAuthorshipsLoading)
                }
                onSelectEvidenceItems={handleSelectDocuments}
                onToggleEvidenceItem={handleToggleDocument}
                selectedEvidenceItemIds={
                  selectedDocumentIds[currentSelectableStep]
                }
                selectedLessons={currentSummary?.selectedLessons ?? 0}
                title={currentStep.shortLabel}
              />
            ) : (
              <InjunctionReportOverview
                rows={overviewRows}
                variant="pending"
                header={labels.overviewHeader}
                pendingSubmittedColumnLabel={labels.pendingSubmittedColumnLabel}
                footnote={
                  getSurplusNotice(
                    getSurplusLessons(countedLessons, requiredLessons),
                    requiredLessons,
                  ) ?? undefined
                }
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PendingAfgorelseDetailView;
